/**
 * Chart Controls Manager
 *
 * Unified JavaScript manager for chart header controls across multiple pages.
 * Handles date pickers, time presets, returns toggle, and state persistence.
 *
 * This consolidates the duplicated control logic from:
 * - /analysis page
 * - /performance-of-factors page
 * - /stock-char page
 *
 * @author JKP Factors Team
 */

class ChartControlsManager {
    constructor(options = {}) {
        // Configuration
        this.config = {
            hasReturnsToggle: options.hasReturnsToggle ?? true,
            defaultReturnsType: options.defaultReturnsType ?? 'raw',
            defaultPreset: options.defaultPreset ?? 'All',
            enablePersistence: options.enablePersistence ?? true,
            storagePrefix: options.storagePrefix ?? 'chartControls',

            // Event callbacks
            onDateChange: options.onDateChange || null,
            onPresetSelect: options.onPresetSelect || null,
            onReturnsToggle: options.onReturnsToggle || null,
            onStateChange: options.onStateChange || null,

            // Date constraints
            minDate: options.minDate || '1926-01-01',
            maxDate: options.maxDate || new Date().toISOString().split('T')[0]
        };

        // Internal state
        this.state = {
            startDate: '',
            endDate: '',
            returnsType: this.config.defaultReturnsType
        };

        // DOM element references
        this.elements = {
            startDateInput: null,
            endDateInput: null,
            returnsRadios: null,
            presetButtons: null,
            container: null
        };

        // Event bus for loose coupling
        this.eventBus = new EventTarget();

        // Bind methods
        this.handleDateChange = this.handleDateChange.bind(this);
        this.handlePresetClick = this.handlePresetClick.bind(this);
        this.handleReturnsToggle = this.handleReturnsToggle.bind(this);
    }

    /**
     * Initialize the controls manager
     * @param {string|HTMLElement} containerSelector - Container selector or element
     * @returns {boolean} Success status
     */
    init(containerSelector = '.chart-controls-unified') {
        try {
            // Find container
            this.elements.container = typeof containerSelector === 'string'
                ? document.querySelector(containerSelector)
                : containerSelector;

            if (!this.elements.container) {
                console.warn('ChartControlsManager: Container not found');
                return false;
            }

            // Cache DOM elements
            this.cacheElements();

            // Load persisted state
            if (this.config.enablePersistence) {
                this.loadPersistedState();
            }

            // Set initial state
            this.applyInitialState();

            // Attach event listeners
            this.attachEventListeners();

            // Initialize date range if not set
            this.initializeDateRange();

            return true;

        } catch (error) {
            console.error('ChartControlsManager initialization failed:', error);
            return false;
        }
    }

    /**
     * Cache DOM element references
     */
    cacheElements() {
        const container = this.elements.container;

        this.elements.startDateInput = container.querySelector('#startDate');
        this.elements.endDateInput = container.querySelector('#endDate');
        this.elements.presetButtons = container.querySelectorAll('.zoom-btn[data-preset]');

        if (this.config.hasReturnsToggle) {
            this.elements.returnsRadios = container.querySelectorAll('input[name="returnsType"]');
        }
    }

    /**
     * Attach event listeners to controls
     */
    attachEventListeners() {
        // Date inputs
        if (this.elements.startDateInput) {
            this.elements.startDateInput.addEventListener('change', this.handleDateChange);
        }
        if (this.elements.endDateInput) {
            this.elements.endDateInput.addEventListener('change', this.handleDateChange);
        }

        // Preset buttons
        this.elements.presetButtons.forEach(button => {
            button.addEventListener('click', this.handlePresetClick);
        });

        // Returns toggle
        if (this.elements.returnsRadios) {
            this.elements.returnsRadios.forEach(radio => {
                radio.addEventListener('change', this.handleReturnsToggle);
            });
        }
    }

    /**
     * Handle date input changes
     * @param {Event} event - Change event
     */
    handleDateChange(event) {
        const { id, value } = event.target;

        if (id === 'startDate') {
            this.state.startDate = value;
        } else if (id === 'endDate') {
            this.state.endDate = value;
        }

        // Validate date range
        if (this.state.startDate && this.state.endDate) {
            if (new Date(this.state.startDate) > new Date(this.state.endDate)) {
                // Swap dates if start > end
                [this.state.startDate, this.state.endDate] = [this.state.endDate, this.state.startDate];
                this.updateDateInputs();
            }
        }

        // Persist and notify
        this.persistState();
        this.notifyStateChange('dateChange', {
            startDate: this.state.startDate,
            endDate: this.state.endDate
        });
    }

    /**
     * Handle preset button clicks
     * @param {Event} event - Click event
     */
    handlePresetClick(event) {
        const button = event.target;
        const preset = button.dataset.preset;

        if (!preset) return;

        // Highlight the selected preset so the user can see which range is applied
        // (exactly one active at a time). CSS already styles .zoom-btn.active.
        this.elements.presetButtons.forEach(b => b.classList.toggle('active', b === button));

        // Calculate date range
        const dateRange = this.calculatePresetRange(preset);
        if (dateRange) {
            this.state.startDate = dateRange.startDate;
            this.state.endDate = dateRange.endDate;

            this.updateDateInputs();
        }

        // Persist and notify
        this.persistState();
        this.notifyStateChange('presetSelect', {
            preset,
            startDate: this.state.startDate,
            endDate: this.state.endDate
        });
    }

    /**
     * Clear the active-state highlight from all zoom presets. Used when the chart resets to
     * full range (e.g. on a tab switch) so the toolbar doesn't keep "5Y" lit while the chart
     * actually shows the full range — matching the no-preset state of a fresh page load.
     */
    clearActivePreset() {
        this.elements.presetButtons?.forEach(b => b.classList.remove('active'));
    }

    /**
     * Handle returns type toggle
     * @param {Event} event - Change event
     */
    handleReturnsToggle(event) {
        this.state.returnsType = event.target.id === 'rawReturns' ? 'raw' : 'market-adjusted';

        // Persist and notify
        this.persistState();
        this.notifyStateChange('returnsToggle', {
            returnsType: this.state.returnsType
        });
    }

    /**
     * Calculate date range for preset
     * @param {string} preset - Preset identifier (1Y, 5Y, 10Y, All)
     * @returns {Object|null} Date range object or null
     */
    calculatePresetRange(preset) {
        const endDate = new Date();
        let startDate = new Date();

        switch (preset) {
            case '1Y':
                startDate.setFullYear(endDate.getFullYear() - 1);
                break;
            case '5Y':
                startDate.setFullYear(endDate.getFullYear() - 5);
                break;
            case '10Y':
                startDate.setFullYear(endDate.getFullYear() - 10);
                break;
            case 'All':
                // Parse the minDate string properly (YYYY-MM-DD format)
                const [year, month, day] = this.config.minDate.split('-').map(Number);
                startDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
                break;
            default:
                return null;
        }

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    }

    /**
     * Update date input values
     */
    updateDateInputs() {
        if (this.elements.startDateInput) {
            this.elements.startDateInput.value = this.state.startDate;
        }
        if (this.elements.endDateInput) {
            this.elements.endDateInput.value = this.state.endDate;
        }
    }

    /**
     * Initialize default date range
     */
    initializeDateRange() {
        if (!this.state.startDate || !this.state.endDate) {
            const defaultRange = this.calculatePresetRange(this.config.defaultPreset);
            if (defaultRange) {
                this.state.startDate = defaultRange.startDate;
                this.state.endDate = defaultRange.endDate;

                this.updateDateInputs();
            }
        }
    }

    /**
     * Apply initial state to UI
     */
    applyInitialState() {
        // Apply returns type
        if (this.config.hasReturnsToggle && this.elements.returnsRadios) {
            const targetRadio = this.state.returnsType === 'raw' ? 'rawReturns' : 'marketAdjusted';
            const radio = document.getElementById(targetRadio);
            if (radio) radio.checked = true;
        }
    }

    /**
     * Persist state to localStorage
     */
    persistState() {
        if (!this.config.enablePersistence) return;

        try {
            const stateToStore = {
                ...this.state,
                timestamp: Date.now()
            };
            localStorage.setItem(
                `${this.config.storagePrefix}_state`,
                JSON.stringify(stateToStore)
            );
        } catch (error) {
            console.warn('Failed to persist chart controls state:', error);
        }
    }

    /**
     * Load persisted state from localStorage
     */
    loadPersistedState() {
        if (!this.config.enablePersistence) return;

        try {
            const stored = localStorage.getItem(`${this.config.storagePrefix}_state`);
            if (stored) {
                const parsedState = JSON.parse(stored);

                // Validate stored state (ignore if older than 24 hours)
                if (parsedState.timestamp && (Date.now() - parsedState.timestamp < 24 * 60 * 60 * 1000)) {
                    this.state = { ...this.state, ...parsedState };
                }
            }
        } catch (error) {
            console.warn('Failed to load persisted chart controls state:', error);
        }
    }

    /**
     * Notify state changes via events and callbacks
     * @param {string} changeType - Type of change
     * @param {Object} data - Event data
     */
    notifyStateChange(changeType, data) {
        // Emit custom event
        const event = new CustomEvent(`chartControls:${changeType}`, {
            detail: { ...data, state: { ...this.state } }
        });
        this.eventBus.dispatchEvent(event);

        // Also dispatch on document for global listening
        document.dispatchEvent(event);

        // Call specific callback
        const callbackMap = {
            dateChange: this.config.onDateChange,
            presetSelect: this.config.onPresetSelect,
            returnsToggle: this.config.onReturnsToggle
        };

        const callback = callbackMap[changeType];
        if (callback && typeof callback === 'function') {
            callback(data, this.state);
        }

        // Call general state change callback
        if (this.config.onStateChange && typeof this.config.onStateChange === 'function') {
            this.config.onStateChange(changeType, data, this.state);
        }
    }

    /**
     * Get current state
     * @returns {Object} Current state
     */
    getState() {
        return { ...this.state };
    }

    /**
     * Set state programmatically
     * @param {Object} newState - New state values
     * @param {boolean} notify - Whether to notify of changes
     */
    setState(newState, notify = true) {
        const oldState = { ...this.state };
        this.state = { ...this.state, ...newState };

        // Update UI
        this.updateDateInputs();

        if (newState.returnsType) {
            this.applyInitialState();
        }

        // Persist
        this.persistState();

        // Notify if requested
        if (notify) {
            this.notifyStateChange('stateUpdate', { oldState, newState: this.state });
        }
    }

    /**
     * Add event listener to the controls manager
     * @param {string} eventType - Event type (dateChange, presetSelect, returnsToggle)
     * @param {Function} handler - Event handler
     */
    addEventListener(eventType, handler) {
        this.eventBus.addEventListener(`chartControls:${eventType}`, handler);
    }

    /**
     * Remove event listener
     * @param {string} eventType - Event type
     * @param {Function} handler - Event handler to remove
     */
    removeEventListener(eventType, handler) {
        this.eventBus.removeEventListener(`chartControls:${eventType}`, handler);
    }

    /**
     * Destroy the controls manager and clean up
     */
    destroy() {
        // Remove event listeners
        if (this.elements.startDateInput) {
            this.elements.startDateInput.removeEventListener('change', this.handleDateChange);
        }
        if (this.elements.endDateInput) {
            this.elements.endDateInput.removeEventListener('change', this.handleDateChange);
        }

        this.elements.presetButtons.forEach(button => {
            button.removeEventListener('click', this.handlePresetClick);
        });

        if (this.elements.returnsRadios) {
            this.elements.returnsRadios.forEach(radio => {
                radio.removeEventListener('change', this.handleReturnsToggle);
            });
        }

        // Clear references
        this.elements = {};
        this.eventBus = null;

    }
}

// ES6 Export
export default ChartControlsManager;

// Also make it globally available like ChartFactory
window.ChartControlsManager = ChartControlsManager;